import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  Stack,
  Divider,
  useScrollTrigger,
} from "@mui/material";

import { useTheme, alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { motion } from "framer-motion";
import { Link as RouterLink, useLocation } from "react-router-dom";
import DownloadAppModal from "./DownloadApp";

const navItems = [
  { label: "Home", path: "/" },
  { label: "Products", path: "/products" },
  { label: "Calculators", path: "/main-calculator" },
  { label: "About Us", path: "/aboutus" },
  { label: "Contact Us", path: "/ContactUs" },
  { label: "Disclosures", path: "/Disclosures" },
];

const isPathActive = (current, target) => {
  const c = current.toLowerCase();
  const t = target.toLowerCase();

  if (t === "/") return c === "/";

  return c === t || c.startsWith(`${t}/`);
};

const Logo = ({ size = 23 }) => {
  const theme = useTheme();

  return (
    <Box
      component={RouterLink}
      to="/"
      aria-label="ArthGyan home"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        textDecoration: "none",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: size,
          fontWeight: 800,
          letterSpacing: "-0.8px",
          color: theme.palette.text.primary,
          lineHeight: 1.2,
        }}
      >
        Arth
        <Box
          component="span"
          sx={{ color: theme.palette.primary.main }}
        >
          Gyan
        </Box>
      </Typography>
    </Box>
  );
};

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [hovered, setHovered] = useState(null);

  const scrolled = useScrollTrigger({
    disableHysteresis: true,
    threshold: 12,
  });

  const primary = theme.palette.primary.main;
  const surface =
    theme.palette.secondary?.main ||
    theme.palette.background.paper;

  const tint =
    theme.palette.tertiary?.main ||
    alpha(primary, 0.08);

  useEffect(() => {
    setOpen(false);
    setHovered(null);
  }, [location.pathname]);

  const openModal = () => {
    setOpen(false);
    setShowDownloadModal(true);
  };

  const downloadButtonSx = {
    px: 2.4,
    py: 1.15,
    borderRadius: "12px",
    fontSize: 14,
    fontWeight: 600,
    color: "#ffffff",
    textTransform: "none",
    whiteSpace: "nowrap",
    background: `linear-gradient(135deg, ${primary}, ${theme.palette.primary.dark || primary
      })`,
    boxShadow: `0 5px 16px ${alpha(primary, 0.24)}`,
    transition: "all 0.25s ease",
    "&:hover": {
      background: theme.palette.primary.dark || primary,
      boxShadow: `0 8px 24px ${alpha(primary, 0.34)}`,
    },
  };

  return (
    <>
      {/* NAVBAR */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: alpha(
            surface,
            scrolled ? 0.96 : 0.98
          ),
          backdropFilter: "blur(18px) saturate(160%)",
          WebkitBackdropFilter: "blur(18px) saturate(160%)",
          borderBottom: `1px solid ${alpha(primary, 0.10)}`,
          boxShadow: scrolled
            ? "0 5px 24px rgba(15, 23, 42, 0.07)"
            : "none",
          color: theme.palette.text.primary,
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar
          sx={{
            maxWidth: 1300,
            mx: "auto",
            width: "100%",
            px: { xs: 2, sm: 3, md: 5 },
            minHeight: {
              xs: "62px !important",
              md: scrolled
                ? "66px !important"
                : "76px !important",
            },
            gap: { xs: 1, md: 2 },
            transition: "min-height 0.3s ease",
          }}
        >
          {/* LOGO */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Logo />
          </Box>

          {/* DESKTOP NAVIGATION */}
          <Stack
            direction="row"
            component="nav"
            aria-label="Main navigation"
            onMouseLeave={() => setHovered(null)}
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 0.3,
              flexShrink: 0,
            }}
          >
            {navItems.map((item) => {
              const isActive = isPathActive(
                location.pathname,
                item.path
              );

              return (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  disableRipple
                  onMouseEnter={() =>
                    setHovered(item.label)
                  }
                  aria-current={
                    isActive ? "page" : undefined
                  }
                  sx={{
                    position: "relative",
                    px: 1.35,
                    py: 1.2,
                    minWidth: 0,
                    borderRadius: "8px",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    fontSize: 13.5,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive
                      ? primary
                      : theme.palette.text.primary,
                    transition: "color 0.25s ease",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: primary,
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 3,
                      left: "50%",
                      width: isActive ? "55%" : "0%",
                      height: "2px",
                      borderRadius: 2,
                      backgroundColor: primary,
                      transform: "translateX(-50%)",
                      transition: "width 0.25s ease",
                    },
                    "&:hover::after": {
                      width: "55%",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>

          {/* DESKTOP DOWNLOAD CTA */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              ml: { md: 1.5 },
              flexShrink: 0,
            }}
          >
            <motion.div
              whileHover={{ scale: 1.035, y: -1 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                onClick={openModal}
                startIcon={
                  <FileDownloadOutlinedIcon
                    sx={{ fontSize: 19 }}
                  />
                }
                sx={downloadButtonSx}
              >
                Download App
              </Button>
            </motion.div>
          </Box>

          {/* MOBILE MENU BUTTON */}
          <IconButton
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            sx={{
              display: { xs: "flex", md: "none" },
              width: 42,
              height: 42,
              ml: 1,
              color: primary,
              border: `1px solid ${alpha(primary, 0.16)}`,
              borderRadius: "12px",
              backgroundColor: alpha(primary, 0.06),
              "&:hover": {
                backgroundColor: alpha(primary, 0.12),
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* MOBILE DRAWER */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 310,
            maxWidth: "88vw",
            backgroundColor: surface,
            borderTopLeftRadius: 22,
            borderBottomLeftRadius: 22,
            borderLeft: `1px solid ${alpha(primary, 0.10)}`,
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          {/* Drawer header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Logo size={22} />

            <IconButton
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                backgroundColor: alpha(
                  theme.palette.text.primary,
                  0.05
                ),
                "&:hover": {
                  backgroundColor: alpha(primary, 0.10),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider
            sx={{
              my: 2.5,
              borderColor: alpha(primary, 0.12),
            }}
          />

          {/* Drawer navigation */}
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "1.5px",
              color: theme.palette.text.secondary,
              mb: 1.5,
              px: 1,
            }}
          >
            MENU
          </Typography>

          <Stack spacing={0.7} sx={{ flexGrow: 1 }}>
            {navItems.map((item, index) => {
              const isActive = isPathActive(
                location.pathname,
                item.path
              );

              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: 16 }}
                  animate={
                    open
                      ? { opacity: 1, x: 0 }
                      : { opacity: 0, x: 16 }
                  }
                  transition={{
                    delay: open
                      ? 0.04 * index
                      : 0,
                    duration: 0.22,
                  }}
                >
                  <Button
                    fullWidth
                    component={RouterLink}
                    to={item.path}
                    onClick={() => setOpen(false)}
                    aria-current={
                      isActive ? "page" : undefined
                    }
                    endIcon={
                      <ArrowForwardRoundedIcon
                        sx={{
                          fontSize: 18,
                          opacity: isActive ? 1 : 0.45,
                        }}
                      />
                    }
                    sx={{
                      justifyContent: "space-between",
                      px: 1.8,
                      py: 1.35,
                      fontSize: 15,
                      fontWeight: isActive ? 700 : 500,
                      textTransform: "none",
                      textAlign: "left",
                      borderRadius: "12px",
                      color: isActive
                        ? primary
                        : theme.palette.text.primary,
                      backgroundColor: isActive
                        ? tint
                        : "transparent",
                      border: `1px solid ${isActive
                        ? alpha(primary, 0.12)
                        : "transparent"
                        }`,
                      "&:hover": {
                        backgroundColor: alpha(
                          primary,
                          0.07
                        ),
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                </motion.div>
              );
            })}
          </Stack>

          {/* Drawer download CTA */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: "18px",
              background: `linear-gradient(145deg, ${alpha(
                primary,
                0.08
              )}, ${alpha(primary, 0.025)})`,
              border: `1px solid ${alpha(primary, 0.10)}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              Manage money smarter
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.7,
                color: theme.palette.text.secondary,
                mb: 2,
              }}
            >
              Explore ArthGyan on your mobile.
            </Typography>

            <Button
              fullWidth
              onClick={openModal}
              startIcon={<FileDownloadOutlinedIcon />}
              sx={{
                ...downloadButtonSx,
                py: 1.25,
                borderRadius: "12px",
              }}
            >
              Download App
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* DOWNLOAD APP MODAL */}
      <DownloadAppModal
        open={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
      />
    </>
  );
};

export default Navbar;
